import type { RxDocument } from 'rxdb';
import type { Note } from '@flintmint/core-shared/rxdb';
import type { GenerationUsageMessageMetadata } from '@flintmint/core-shared/message-metadata';
import { stripHtml } from '@flintmint/core-shared/utils';

import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { capture, type AnalyticsEventProperties } from '@/analytics';
import { localDataService } from '@/providers/rxdb/local-data-service';
import { hasReviewCounters, type PendingReview } from '../review/persisted-review';
import { isNoteDocument, type PersistedDocument } from '../document-utils';

// Session store for note-generation runs, keyed by requestId (the note document id). It also owns
// the PostHog emit helpers. The store must NOT import from agent-run-store (agent-run-store
// imports recordGenerationStart from here); anything from that scope is passed in by the call site.

export type NoteGenerationFailureType = AnalyticsEventProperties<'note_generation_failed'>['error_type'];

// Curated note attributes diffed for the note_edited_by_user event. Derived/noise/identity fields
// (parsedContent, numTokens, updatedAt, createdAt, classificationCache, pendingReview, id, userId,
// templates, _rev, _deleted) are intentionally excluded.
const DIFFABLE_FIELDS = [
  'content',
  'name',
  'tagIds',
  'blockIds',
  'variant',
  'templateId',
  'aliases',
  'instructions',
  'blockInstructions',
  'userSources',
] as const;

type NoteGenerationBaseline = Pick<Note, (typeof DIFFABLE_FIELDS)[number]>;

export type NoteGenerationSession = {
  startedAtMs: number;
  completedAtMs?: number;
  // Request start -> first streamed content rendered on screen, in ms. Latched once per session by
  // recordFirstContent, which is only reachable on streaming-preview runs, so the field stays absent
  // when the streaming toggle is off. Cleared naturally: recordGenerationStart replaces the session.
  firstContentMs?: number;
  // Post-generation note state, consumed later by the note-edited diff. Stays in memory only and
  // never enters an event payload.
  baseline?: NoteGenerationBaseline;
  // User-edit burst tracking. Reset whenever a fresh baseline is written so the edited event can
  // fire at most once per baseline.
  editedEventFired?: boolean;
  firstEditAtMs?: number;
  lastEditAtMs?: number;
  lastEditSnapshot?: NoteGenerationBaseline;
};

const EDIT_IDLE_MS = 60_000;

const noteGenerationSessions = new Map<string, NoteGenerationSession>();
// Module-level so the trailing timer survives component unmount / note switching.
const editBurstTimers = new Map<string, ReturnType<typeof setTimeout>>();

export function recordGenerationStart(requestId: string): void {
  // Cancel any pending user-edit burst so a stale burst never fires mid-generation against a
  // soon-to-be-replaced baseline.
  clearEditBurstTimer(requestId);
  noteGenerationSessions.set(requestId, { startedAtMs: Date.now() });
}

// Pure observer for the streamed editor preview: called when the first streamed content of a run is
// rendered on screen. Latches once per session (the session is keyed by requestId at store level),
// so duplicate streaming-effect instances for the same requestId cannot double-fire or record
// different values. A second recordGenerationStart replaces the session, re-arming the latch.
export function recordFirstContent(requestId: string): void {
  try {
    const session = noteGenerationSessions.get(requestId);
    if (!session || session.firstContentMs != null) {
      return;
    }

    session.firstContentMs = Date.now() - session.startedAtMs;
  } catch {
    // Telemetry must never throw or affect streaming.
  }
}

// Passthrough tap for a live editor stream (prompt-mode generate-text): calls recordFirstContent
// once on the first non-empty chunk entering the toolkit's streamHtml. The toolkit applies that
// chunk essentially immediately, so this is the moment streamed content becomes visible. Only used
// on the streaming branch; the buffered (streaming-off) branch stays untapped so the field remains
// absent, matching the chat path's semantics.
export function tapFirstContent(requestId: string, stream: ReadableStream<string>): ReadableStream<string> {
  return stream.pipeThrough(
    new TransformStream<string, string>({
      transform(chunk, controller) {
        if (chunk !== '') {
          recordFirstContent(requestId);
        }
        controller.enqueue(chunk);
      },
    })
  );
}

export function recordUserEdit(requestId: string, document: PersistedDocument): void {
  try {
    const session = noteGenerationSessions.get(requestId);
    if (!session || !session.baseline || !isNoteDocument(document) || session.editedEventFired) {
      return;
    }

    const now = Date.now();
    session.firstEditAtMs ??= now;
    session.lastEditAtMs = now;
    session.lastEditSnapshot = buildGenerationBaseline(document);

    clearEditBurstTimer(requestId);
    editBurstTimers.set(
      requestId,
      setTimeout(() => emitNoteEdited(requestId), EDIT_IDLE_MS)
    );
  } catch {
    // Telemetry must never throw or affect the editor.
  }
}

export function captureNoteGeneration({
  requestId,
  document,
  outputCharCount,
  promptInputCharCount,
  targetedEdit,
  streamingEnabled,
  usage,
}: {
  requestId: string;
  document?: PersistedDocument;
  outputCharCount: number;
  promptInputCharCount: number;
  targetedEdit: boolean;
  streamingEnabled: boolean;
  usage?: GenerationUsageMessageMetadata;
}): void {
  try {
    const session = noteGenerationSessions.get(requestId);
    if (!document || !isNoteDocument(document) || !session) {
      return;
    }

    recordGenerationComplete(session, requestId, document);
    emitNoteGeneration({ session, document, outputCharCount, promptInputCharCount, targetedEdit, streamingEnabled, usage });
  } catch {
    // Telemetry must never throw or affect the generation run.
  }
}

// Session/baseline bookkeeping for a completed generation.
function recordGenerationComplete(session: NoteGenerationSession, requestId: string, document: RxDocument<Note>): void {
  session.completedAtMs = Date.now();
  session.baseline = buildGenerationBaseline(document);
  // Re-arm the edited event for this fresh baseline, covering a second generation on an existing
  // session object (a new session object from recordGenerationStart already starts clear).
  resetEditBurst(session, requestId);
}

function emitNoteGeneration({
  session,
  document,
  outputCharCount,
  promptInputCharCount,
  targetedEdit,
  streamingEnabled,
  usage,
}: {
  session: NoteGenerationSession;
  document: RxDocument<Note>;
  outputCharCount: number;
  promptInputCharCount: number;
  targetedEdit: boolean;
  streamingEnabled: boolean;
  usage?: GenerationUsageMessageMetadata;
}): void {
  capture('note_generation', {
    note_id: document.id,
    is_from_template: document.templateId != null,
    generation_time_ms: session.completedAtMs! - session.startedAtMs,
    ...(session.firstContentMs != null ? { time_to_first_content_ms: session.firstContentMs } : {}),
    output_char_count: outputCharCount,
    prompt_input_char_count: promptInputCharCount,
    targeted_edit: targetedEdit,
    streaming_enabled: streamingEnabled,
    ...buildUsageFields(usage),
  });

  void maybeCaptureFirstNoteGenerated(document.id);
}

// Emits first_note_generated once per user, gated by a flag on the synced user doc. Write-before-emit:
// a failed write suppresses the event rather than risking a repeat. Fully guarded so neither a sync
// throw nor a promise rejection can affect the generation run.
async function maybeCaptureFirstNoteGenerated(noteId: string): Promise<void> {
  try {
    const { id: userId } = getAuthUser();
    const userDoc = await localDataService.getUserDocument(userId);
    if (!userDoc || userDoc.analytics?.firstNoteGeneratedAt) {
      return;
    }

    await userDoc.incrementalPatch({
      analytics: { ...userDoc.analytics, firstNoteGeneratedAt: new Date().toISOString() },
    });

    capture('first_note_generated', {
      note_id: noteId,
      time_since_signup_minutes: Math.round((Date.now() - Date.parse(userDoc.createdAt)) / 60000),
    });
  } catch {
    // Telemetry must never throw or affect the generation run.
  }
}

export function captureNoteGenerationFailed({
  requestId,
  document,
  errorType,
  promptInputCharCount,
}: {
  requestId: string;
  document?: PersistedDocument;
  errorType: NoteGenerationFailureType;
  promptInputCharCount: number;
}): void {
  try {
    const session = noteGenerationSessions.get(requestId);
    if (!document || !isNoteDocument(document) || !session) {
      return;
    }

    capture('note_generation_failed', {
      note_id: document.id,
      error_type: errorType,
      time_to_failure_ms: Date.now() - session.startedAtMs,
      prompt_input_char_count: promptInputCharCount,
    });

    // A failed generation is terminal: the session has no baseline and no future consumer, so delete
    // it (mirroring emitNoteEdited). A later recordUserEdit no-ops and a new generation re-creates it
    // via recordGenerationStart.
    noteGenerationSessions.delete(requestId);
  } catch {
    // Telemetry must never throw or affect the generation run.
  }
}

// Emit-only telemetry for a fully resolved post-generation review. Notes only, and skipped for
// reviews persisted before counters existed so we never send garbage numbers. Never throws.
export function captureNoteGenerationReviewed({
  document,
  pendingReview,
  acceptedCount,
}: {
  document?: PersistedDocument;
  pendingReview: PendingReview | null;
  acceptedCount: number;
}): void {
  try {
    if (!document || !isNoteDocument(document) || !pendingReview) {
      return;
    }

    if (!hasReviewCounters(pendingReview)) {
      return;
    }

    const { totalCount, createdAtMs } = pendingReview;
    const suggestionsAccepted = acceptedCount;

    capture('note_generation_reviewed', {
      note_id: document.id,
      time_since_generation_sec: Math.round((Date.now() - createdAtMs) / 1000),
      suggestions_accepted: suggestionsAccepted,
      suggestions_rejected: totalCount - suggestionsAccepted,
    });
  } catch {
    // Telemetry must never throw or affect review behavior.
  }
}

function emitNoteEdited(requestId: string): void {
  try {
    editBurstTimers.delete(requestId);

    const session = noteGenerationSessions.get(requestId);
    if (
      !session ||
      !session.baseline ||
      !session.lastEditSnapshot ||
      session.editedEventFired ||
      session.firstEditAtMs == null ||
      session.lastEditAtMs == null
    ) {
      return;
    }

    session.editedEventFired = true;
    const { baseline, lastEditSnapshot } = session;

    capture('note_edited_by_user', {
      note_id: requestId,
      edit_duration_sec: Math.round((session.lastEditAtMs - session.firstEditAtMs) / 1000),
      fields_changed: diffChangedFields(baseline, lastEditSnapshot),
      ...buildContentCharDelta(baseline.content, lastEditSnapshot.content),
      ...buildNameCharDelta(baseline.name, lastEditSnapshot.name),
    });

    // The edited event is the baseline's terminal consumer. Delete the session so a later
    // recordUserEdit no-ops (no session) and a new generation re-creates it via recordGenerationStart.
    noteGenerationSessions.delete(requestId);
  } catch {
    // Telemetry must never throw or affect the editor.
  }
}

function diffChangedFields(baseline: NoteGenerationBaseline, snapshot: NoteGenerationBaseline): string[] {
  return DIFFABLE_FIELDS.filter((field) => JSON.stringify(baseline[field]) !== JSON.stringify(snapshot[field]));
}

function buildContentCharDelta(before: string, after: string) {
  const { added, removed } = charDelta(stripHtml(before), stripHtml(after));
  return { content_chars_added: added, content_chars_removed: removed };
}

function buildNameCharDelta(before: string, after: string) {
  const { added, removed } = charDelta(before, after);
  return { name_chars_added: added, name_chars_removed: removed };
}

// Common-prefix/common-suffix delta. Only counts leave this module; never the underlying strings.
function charDelta(before: string, after: string): { added: number; removed: number } {
  const maxCommon = Math.min(before.length, after.length);

  let prefix = 0;
  while (prefix < maxCommon && before[prefix] === after[prefix]) {
    prefix += 1;
  }

  let suffix = 0;
  while (suffix < maxCommon - prefix && before[before.length - 1 - suffix] === after[after.length - 1 - suffix]) {
    suffix += 1;
  }

  const common = prefix + suffix;
  return { added: after.length - common, removed: before.length - common };
}

function buildGenerationBaseline(document: RxDocument<Note>): NoteGenerationBaseline {
  return {
    name: document.name,
    content: document.content,
    tagIds: [...(document.tagIds ?? [])],
    blockIds: [...(document.blockIds ?? [])],
    variant: document.variant,
    templateId: document.templateId,
    aliases: [...(document.aliases ?? [])],
    instructions: { ...document.instructions },
    blockInstructions: { ...document.blockInstructions },
    userSources: { ...document.userSources },
  };
}

function resetEditBurst(session: NoteGenerationSession, requestId: string): void {
  session.editedEventFired = false;
  session.firstEditAtMs = undefined;
  session.lastEditAtMs = undefined;
  session.lastEditSnapshot = undefined;
  clearEditBurstTimer(requestId);
}

function clearEditBurstTimer(requestId: string): void {
  const timer = editBurstTimers.get(requestId);
  if (timer) {
    clearTimeout(timer);
    editBurstTimers.delete(requestId);
  }
}

function buildUsageFields(usage?: GenerationUsageMessageMetadata) {
  return {
    ...(usage?.modelId != null ? { model_id: usage.modelId } : {}),
    ...(usage?.provider != null ? { provider: usage.provider } : {}),
    ...(usage?.tokensIn != null ? { tokens_in: usage.tokensIn } : {}),
    ...(usage?.tokensOut != null ? { tokens_out: usage.tokensOut } : {}),
  };
}
